import { combineLatest } from 'rxjs';
import { Injectable } from '@angular/core';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { ShopUtils } from '@app/utilities/shop.util';
import { ShopStoreSelector } from '@app/common/selectors/shop.selector';

import { IShopCatalog } from '@core/models/shop.model';
import {
  DoodProductModel,
  ICategory,
  TreeCatalogCategoryWithCategoryEntity,
  TreeCategoryWithProduct,
} from '@core/models/product.model';
import {
  linkTreeCatalogCategoryWithCategoryEntity,
  toCatalogCategories,
} from '@shared/utils/tree/tree-to-tree-tabs';
import { DoodCategoryWithProduct } from '@shared/interfaces/category.interface';
import {
  categoryHasVisibleProductInSubtree,
  removeCategoriesWithoutVisibleProducts,
} from './category-tree-visibility';

@Injectable({ providedIn: 'root' })
export class ShopStoreRefiner {
  // Active
  selectActiveShopProducts = this.selector.selectActive$.pipe(
    map(_active => _active?.products?.filter(p => !p.hidden && p.id && p.name) ?? []),
  );

  selectActiveShopAdditionGroups = this.selector.selectActive$.pipe(
    map(_active => _active?.addition_groups ?? []),
  );

  selectActiveShopAvailableStores = this.selector.selectActive$.pipe(
    map(_active => _active?.store.filter(_shop => _shop.available) ?? []),
  );

  selectAllActiveShopStores = this.selector.selectActive$.pipe(
    map(_active => _active?.store ?? []),
  );

  selectActiveShopEnabledCatalogs = combineLatest([
    this.selector.selectActive$,
    this.selector.selectActiveFilterCatalogIds$,
  ]).pipe(
    map(([_active, requestedIds]) => {
      const _catalogs = _active?.catalogs ?? [];
      const _enabledCatalogs = _active?.enabled_catalogs ?? [];
      const _foundCatalogs: IShopCatalog[] = [];

      _enabledCatalogs.forEach(id => {
        const _found = _catalogs.find(catalog => catalog.id === id);
        if (_found) _foundCatalogs.push(_found);
      });

      const sorted =
        [..._foundCatalogs].sort((a, b) => (a.position as number) - (b.position as number)) ?? [];

      if (!requestedIds.length) {
        return sorted;
      }

      const filtered = sorted.filter(catalog => requestedIds.includes(catalog.id));
      return filtered.length ? filtered : sorted;
    }),
    distinctUntilChanged((a, b) => a.map(c => c.id).join(',') === b.map(c => c.id).join(',')),
  );

  selectActiveShopEnabledStores = combineLatest([
    this.selectActiveShopEnabledCatalogs,
    this.selectActiveShopAvailableStores,
  ]).pipe(
    map(([catalogs, stores]) => {
      const enabledStores: ICategory[] = [];
      catalogs.forEach(catalog => {
        catalog.stores.map((id: string) => {
          if (stores.find(store => store.id === id)) {
            const _found = stores.find(store => store.id === id);
            if (_found) enabledStores.push(_found as ICategory);
          }
        });
      });
      return enabledStores;
    }),
    distinctUntilChanged(),
  );

  /**
   * Return the tree of catalog categories associated with their category entity.
   * Useful for displaying the catalog categories in a tree structure.
   */
  selectActiveShopEnabledTreeCatalogCategory = combineLatest([
    this.selectActiveShopEnabledCatalogs,
    this.selectActiveShopAvailableStores,
    this.selectActiveShopProducts,
  ]).pipe(
    map(([catalogs, stores, products]) => {
      const treeCatalogCategories = catalogs
        .map(catalog => {
          const catalogsCategories = catalog.categories?.length
            ? catalog.categories
            : toCatalogCategories(catalog.stores); // Some shop may not have categories, it was created after the stores. It is used to keep the backward compatibility.
          return linkTreeCatalogCategoryWithCategoryEntity(catalogsCategories, stores);
        })
        .flat();

      // When products are not loaded yet (e.g. on page reload), keep full tree so category titles stay visible
      if (!products?.length) {
        return treeCatalogCategories;
      }
      // Keep a category when it has visible products, or a descendant still does.
      return removeCategoriesWithoutVisibleProducts(treeCatalogCategories, products);
    }),
  );

  selectActiveShopStores = combineLatest([this.selectActiveShopAvailableStores]).pipe(
    map(([stores]) => {
      return stores;
    }),
  );

  selectActiveProductsCategories = combineLatest([
    this.selectActiveShopEnabledTreeCatalogCategory,
    this.selectActiveShopProducts,
    this.selector.selectActive$,
  ]).pipe(
    debounceTime(20),
    map(([treeCatalogCategories, products, active]) => {
      if (active) {
        return this.toTreeCategoriesWithProducts(treeCatalogCategories, products, active.id);
      }
      return [];
    }),
    map(categoryWithProductTrees =>
      categoryWithProductTrees.filter(
        categoryWithProductTree =>
          categoryWithProductTree.category.products?.length ||
          categoryWithProductTree.children.length,
      ),
    ),
  );

  selectActiveFlattenProductsWithCategories = combineLatest([
    this.selectActiveShopEnabledTreeCatalogCategory,
    this.selectActiveShopProducts,
    this.selector.selectActive$,
  ]).pipe(
    debounceTime(20),
    map(([treeCatalogCategories, products, active]) => {
      if (!active) {
        return [];
      }
      const flattened = this.flattenTreeCatalogCategoriesToCategoriesWithProducts(
        treeCatalogCategories,
        products,
        active.id,
      );
      if (!products?.length) {
        return flattened.filter(category => category.products?.length);
      }
      return flattened.filter(
        category =>
          !!category.productsInformation?.length || !!category.hasDescendantWithVisibleProducts,
      );
    }),
  );

  selectActiveShopEnabledDistributionMode = this.selector.selectActive$.pipe(
    map(shop => {
      return shop?.distribution_modes?.filter(mode => mode.enabled) ?? [];
    }),
  );

  selectProductsByCategoryId(categoryId: string | undefined) {
    return combineLatest([
      this.selectActiveShopEnabledStores,
      this.selectActiveShopProducts,
      this.selector.selectActive$,
    ]).pipe(
      map(([stores, products, active]) => {
        if (active) {
          const categories = ShopUtils.joinItems(stores, products, active.id);
          const category = categories.find(cat => cat.id === categoryId);
          return category?.products;
        }
        return null;
      }),
    );
  }
  selectCategoryById(categoryId: string) {
    return combineLatest([
      this.selectActiveShopEnabledStores,
      this.selectActiveShopProducts,
      this.selector.selectActive$,
    ]).pipe(
      map(([stores, products, active]) => {
        if (active) {
          const categories = ShopUtils.joinItems(stores, products, active.id);
          const category = categories.find(cat => cat.id === categoryId);
          return category || null;
        }
        return null;
      }),
    );
  }

  selectCategories = combineLatest([
    this.selectActiveShopStores,
    this.selectActiveShopProducts,
    this.selector.selectActive$,
  ]).pipe(
    map(([stores, products, active]) => {
      if (active) {
        return ShopUtils.joinItems(stores, products, active.id);
      }
      return null;
    }),
  );

  // Products
  selectShopsProducts = this.selector.selectShops.pipe(
    map(shops => {
      return shops.reduce((_products, _shop) => {
        return [..._products, ..._shop.products];
      }, [] as DoodProductModel[]);
    }),
  );

  private toTreeCategoriesWithProducts(
    treeCatalogCategories: TreeCatalogCategoryWithCategoryEntity[],
    products: DoodProductModel[],
    shopId: string,
  ): TreeCategoryWithProduct[] {
    const flattenRecursive = (
      categories: TreeCatalogCategoryWithCategoryEntity[],
    ): TreeCategoryWithProduct[] => {
      return categories.map(treeCatalogCategory => {
        return {
          ...treeCatalogCategory,
          products: treeCatalogCategory.category.products
            ?.map(productId => {
              const foundProduct = products?.find(product => product.id === productId);
              if (!foundProduct) return null;
              return {
                ...foundProduct,
                shop_id: shopId,
              };
            })
            .filter(p => !!p) as DoodProductModel[],
          children: flattenRecursive(treeCatalogCategory.children),
        } satisfies TreeCategoryWithProduct;
      });
    };

    return flattenRecursive(treeCatalogCategories);
  }

  /**
   * Flatten tree catalog categories to categories with products, preserving level information
   */
  private flattenTreeCatalogCategoriesToCategoriesWithProducts(
    treeCatalogCategories: TreeCatalogCategoryWithCategoryEntity[],
    products: DoodProductModel[],
    shopId: string,
  ): DoodCategoryWithProduct[] {
    const flattenRecursive = (
      categories: TreeCatalogCategoryWithCategoryEntity[],
    ): DoodCategoryWithProduct[] => {
      const result: DoodCategoryWithProduct[] = [];

      categories.forEach(treeCatalogCategory => {
        if (treeCatalogCategory.category) {
          const productsInformation = treeCatalogCategory.category.products
            ?.map(productId => {
              const foundProduct = products?.find(product => product.id === productId);
              if (!foundProduct) return null;
              return {
                ...foundProduct,
                shop_id: shopId,
              };
            })
            .filter(p => !!p) as DoodProductModel[];

          const hasDescendantWithVisibleProducts = !!treeCatalogCategory.children?.some(child =>
            categoryHasVisibleProductInSubtree(child, products),
          );

          result.push({
            ...treeCatalogCategory.category,
            level: treeCatalogCategory.level,
            productsInformation,
            hasDescendantWithVisibleProducts,
          });
        }

        // Recursively flatten children
        if (treeCatalogCategory.children?.length) {
          result.push(...flattenRecursive(treeCatalogCategory.children));
        }
      });

      return result;
    };

    return flattenRecursive(treeCatalogCategories);
  }

  constructor(private selector: ShopStoreSelector) {}
}
